import { useAuthStore } from '@/store'

interface DeviceInfo {
  userAgent: string
  platform: string
  language: string  
  viewport: string
  telegram: {
    version?: string
    platform?: string
    colorScheme?: string
  }
}

const getDeviceInfo = (): DeviceInfo => {
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    telegram: {
      version: window.Telegram?.WebApp?.version,
      platform: window.Telegram?.WebApp?.platform,
      colorScheme: window.Telegram?.WebApp?.colorScheme
    }
  }
}



// Массив для хранения последних логов из консоли
let consoleLogHistory: Array<{timestamp: string, level: string, message: string}> = []

// Функция для инициализации перехвата логов консоли
const initConsoleCapture = () => {
  if ((window as any).__consoleOverridden) return // Уже инициализировано
  
  const originalLog = console.log
  const originalWarn = console.warn
  const originalError = console.error
  const originalInfo = console.info
  
  const captureLog = (level: string, originalMethod: any) => {
    return (...args: any[]) => {
      // Вызываем оригинальный метод
      originalMethod.apply(console, args)
      
      // Сохраняем лог для багрепорта
      const message = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
      ).join(' ')
      
      consoleLogHistory.push({
        timestamp: new Date().toISOString(),
        level,
        message: message.slice(0, 300) // Увеличиваем длину логов
      })
      
      // Храним только последние 50 логов
      if (consoleLogHistory.length > 50) {
        consoleLogHistory = consoleLogHistory.slice(-50)
      }
    }
  }
  
  console.log = captureLog('log', originalLog)
  console.warn = captureLog('warn', originalWarn)
  console.error = captureLog('error', originalError)
  console.info = captureLog('info', originalInfo)
  
  ;(window as any).__consoleOverridden = true
}

// Инициализируем перехват при первом импорте модуля
initConsoleCapture()

const getConsoleHistory = (): string[] => {
  // Возвращаем последние 20 логов из консоли
  return consoleLogHistory
    .slice(-20)
    .map(log => {
      const time = new Date(log.timestamp).toLocaleTimeString('ru-RU')
      const levelIcon = {
        'log': '📊',
        'warn': '⚠️', 
        'error': '❌',
        'info': 'ℹ️'
      }[log.level] || '📝'
      
      return `${levelIcon} ${time}: ${log.message}`
    })
}

const generateFeedbackData = () => {
  const deviceInfo = getDeviceInfo()
  const user = useAuthStore.getState().user
  const consoleLogs = getConsoleHistory()
  
  return {
    timestamp: new Date().toISOString(),
    user: {
      id: user?.id,
      telegram_id: user?.telegram_id,
      name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim(),
      username: user?.tg_username
    },
    technical: {
      telegram: deviceInfo.telegram,
      viewport: deviceInfo.viewport,
      url: window.location.href,
      userAgent: deviceInfo.userAgent
    },
    logs: consoleLogs
  }
}

export const sendFeedbackToDeveloper = () => {
  const data = generateFeedbackData()
  
  // Настройки для отправки багрепортов
  const DEVELOPER_USERNAME = 'gufranov_da' // Username разработчика
  
  // Получаем чистый путь без параметров Telegram WebApp
  const cleanUrl = data.technical.url.split('#')[0].split('?')[0]
  const currentPage = window.location.pathname || '/'
  
// Короткое сообщение для URL (чтобы поместилось в лимиты Telegram)
const shortMessage = `🐛 БАГ-РЕПОРТ

👤 ${data.user.name || 'Неизвестно'} ${data.user.username ? `(@${data.user.username})` : ''}
📱 ID: ${data.user.telegram_id}
📄 ${currentPage}
⏰ ${new Date(data.timestamp).toLocaleTimeString('ru-RU')}

📝 ОПИСАНИЕ ПРОБЛЕМЫ:
[Опишите что произошло и прикрепите скрины/видео]`

  // Полное сообщение с техническими деталями (для копирования)
  const fullMessage = `🐛 БАГ-РЕПОРТ

👤 ${data.user.name || 'Неизвестно'} ${data.user.username ? `(@${data.user.username})` : ''}
📱 ID: ${data.user.telegram_id}
⏰ ${new Date(data.timestamp).toLocaleString('ru-RU')}

📱 Telegram v${data.technical.telegram.version || '?'} (${data.technical.telegram.platform || '?'})
📐 ${data.technical.viewport} • ${data.technical.telegram.colorScheme || 'unknown'} theme
📄 Страница: ${currentPage}${data.logs.length > 0 ? `

📋 Последние логи консоли:
${data.logs.slice(-10).join('\n')}` : ''}

📝 ОПИСАНИЕ ПРОБЛЕМЫ:
[Опишите что произошло и прикрепите скрины/видео]

---
User ID: ${data.user.id}
User-Agent: ${data.technical.userAgent}`

  // Создаем ссылку для открытия чата с коротким сообщением
  const telegramUrl = `https://t.me/${DEVELOPER_USERNAME}?text=${encodeURIComponent(shortMessage)}`
  
  try {
    // Копируем полное сообщение в буфер обмена для удобства
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(fullMessage).then(() => {
        console.log('📋 Полная техническая информация скопирована в буфер обмена')
      }).catch(() => {
        console.log('⚠️ Не удалось скопировать в буфер обмена')
      })
    }
    
    // Используем Telegram WebApp метод если доступен
    if (window.Telegram?.WebApp && (window.Telegram.WebApp as any).openTelegramLink) {
      (window.Telegram.WebApp as any).openTelegramLink(telegramUrl)
    } else if (window.Telegram?.WebApp && (window.Telegram.WebApp as any).openLink) {
      // Используем openLink как fallback
      (window.Telegram.WebApp as any).openLink(telegramUrl)
    } else {
      // Fallback к обычному window.open
      window.open(telegramUrl, '_blank')
    }
  } catch (error) {
    console.error('Failed to open feedback chat:', error)
    // Последний fallback
    try {
      window.open(telegramUrl, '_blank')
    } catch (fallbackError) {
      console.error('All telegram link methods failed:', fallbackError)
      // Показываем пользователю сообщение с инструкцией
      alert(`Не удалось открыть чат. Пожалуйста, напишите вручную в Telegram: @${DEVELOPER_USERNAME}\n\nПолная техническая информация скопирована в буфер обмена.\n\nИли используйте это короткое сообщение:\n\n${shortMessage}`)
    }
  }
}

// Функция для добавления простого лога действия (если понадобится в будущем)
export const logUserAction = (action: string, details?: any) => {
  const log = {
    timestamp: new Date().toISOString(),
    action,
    page: window.location.pathname,
    details
  }
  
  try {
    const existingLogs = JSON.parse(localStorage.getItem('app_user_actions') || '[]')
    existingLogs.unshift(log)
    
    // Храним только последние 20 действий
    const recentLogs = existingLogs.slice(0, 20)
    localStorage.setItem('app_user_actions', JSON.stringify(recentLogs))
  } catch (error) {
    console.error('Failed to log user action:', error)
  }
}